import { DisabledPrompt, Link } from "@bcm-dev/bcms-vite";
import React, { useEffect, useState } from "react";
import { useActivityPageContext } from "../..";

import CustomAccordion, { CustomAccordionContent, CustomAccordionToggle, useCustomAccordion } from "compid/custom-ui/CustomAccordion";
import UIButton from "compid/ui/UIButton";
import FilterCustomDateRange from "./components/FilterCustomDateRange";

import { SVGAsset } from "res";
import { DateRangeFilterCustomSelect, SortByFilterCustomSelect, StatusFilterCustomSelect } from "./components/FilterCustomSelect";
import "./styles.css";

const FilterBarContext = React.createContext(null);
export const useFilterBarContext = () => React.useContext(FilterBarContext);

export default function FilterBarWidget(props) {
  // Get the activity page context and get the filters
  const activity = useActivityPageContext();
  const filters = activity.filters;
  const filtered = activity.filtered;
  // Set the defaults for the filter status and date range
  const paymentStatus = filters.status ?? "All";
  const sortType = filters.sort ?? "None";
  let dateRange = "Last 6 Months";
  // Update the date range based on what is selected
  dateRange = filters.date !== undefined ? filters.date.label : dateRange;
  dateRange = filters.custom !== undefined ? filters.custom.label : dateRange;
  // Create a state for the filter widget using the set filters from the activity context
  const [ state, setState ] = useState({ ...filters });
  // Function to return an object with the filters in state besides the property passed to the function
  const ignoreProperty = prop => Object.keys(state).reduce((result, key) => prop === key ? result : { ...result, [key] : state[key] }, {});
  // Functions to update the filters for the date and radio buttons
  const setDateFilter = value => setState({ ...ignoreProperty("custom"), "date": value });
  const setCustomDateFilter = value => setState({ ...ignoreProperty("date"), "custom": value });
  const setRadioStatusFilter = value => setState({ ...state, "status" : value });
  const setRadioSortFilter = value => setState({ ...state, "sort" : value });
  const className = props.className ?? "";

  const controller = {
    state,
    setDateFilter,
    setRadioStatusFilter,
    setRadioSortFilter,
    setCustomDateFilter
  };


  useEffect(() => setState(activity.filters), [ activity ]);

  return(
    <FilterBarContext.Provider value={ controller }>
      <div className={`filter-bar-widget ${className}`}>
        <CustomAccordion>
          {/* Filter Bar */}
          <div className="bar-container flex-container flex-horizontal align-center">
            <div className="filter-item-group set-width filter-item">
              <CustomAccordionToggle>
                <Link className="flex-container flex-horizontal align-center">
                  <SVGAsset id="FilterLinesIcon" className="left-icon blue-icon" />
                  <p className="filters-text" data-point="Activity_Filters_Link">Filter</p>
                </Link>
              </CustomAccordionToggle>
            </div>
            <div className="flex-container flex-horizontal align-center space-between flex-width">
              <div className="left-column flex-container flex-horizontal align-center flex-width">
              <div className="filter-item-group set-width payment-status">
                  <b className="filter-item-label">Status:</b>
                  <span>{ paymentStatus }</span>
                </div>
                <div className="filter-item-group set-width payment-status">
                  <b className="filter-item-label">Sort by:</b>
                  <span>{ sortType }</span>
                </div>
                <div className="filter-item-group set-width">
                  <b className="filter-item-label">Date range:</b>
                  <span>{ dateRange }</span>
                </div>
              </div>
              { filtered.length > 0 &&
                <div className="right-column flex-container flex-horizontal align-center set-width">
                  <div className="filter-item-group">
                    <DisabledPrompt>
                      <Link to="/" className="flex-container flex-horizontal align-center">
                        <SVGAsset id="DownloadIcon" className="left-icon" />
                        <p data-point="Activity_Filter_Download">Download</p>
                      </Link>
                    </DisabledPrompt>
                  </div>
                  <div className="filter-item-group">
                    <DisabledPrompt>
                      <Link to="/" className="flex-container flex-horizontal align-center">
                        <SVGAsset id="PrinterIcon" className="left-icon" />
                        <p data-point="Activity_Filter_Print">Print</p>
                      </Link>
                    </DisabledPrompt>
                  </div>
                </div>
              }
            </div>
          </div>

          {/* Toggled Content */}
          <CustomAccordionContent>
            <div className="filter-toggle-content flex-container flex-vertical">
              <div className="filterable-options-container flex-container flex-horizontal">

                {/* Status */}
                <div className="status-container toggle-column flex-width gray-border-right">
                  <div className="header-container">
                    <b>
                      <span data-point="Activity_Filter_Status">Status</span>
                    </b>
                  </div>
                  <StatusFilterCustomSelect />
                </div>

                {/* Sort by */}
                <div className="sort-by-container toggle-column flex-width">
                  <div className="header-container">
                    <b>
                      <span data-point="Activity_Filter_Sort_By">Sort by</span>
                    </b>
                  </div>
                  <SortByFilterCustomSelect />
                </div>

                {/* Date Range */}
                <div className="date-range-container flex-width toggle-column gray-border-right">
                  <div className="header-container">
                    <b>
                      <span data-point="Activity_Filter_Date_Range">Date range</span>
                    </b>
                  </div>
                  <DateRangeFilterCustomSelect />
                </div>

              </div>

              {/* FilterCustomDateRange Reference */}
              <div className="custom-date-input-container justify-right flex-container flex-horizontal align-center">
                <FilterCustomDateRange>Custom date range</FilterCustomDateRange>
              </div>
              
              <div className="flex-container flex-container align-center button-text-container">
                {/* Info Text */}
                <div className="info-text set-width flex-container justify-left">
                  <div className="flex-container flex-horizontal align-center">
                    <SVGAsset id="UpsellIcon" className="icon set-width info-icon" />
                    <p className="flex-width info-text sub-text">Click Apply to change filter settings or Reset to start over.</p>
                  </div>
                </div>

                {/* Buttons */}
                <div className="buttons-container flex-container set-width justify-right">
                  <FilterBarButtons />
                </div>
              </div>
              
            </div>
          </CustomAccordionContent>
        </CustomAccordion>
      </div>
    </FilterBarContext.Provider>
  );
}

/**
 * @function FilterBarButtons
 * @return {React.Component}
 * @description Component to have the buttons for the widget in order to access the custom accordion context
 * to close the accordion when either the reset or apply buttons are clicked
 * @example <FilterBarButtons />
 */
function FilterBarButtons(props) {
  const activity = useActivityPageContext();
  const { state } = useFilterBarContext();
  const { close } = useCustomAccordion();

  const resetBtn = evt =>{
    activity.reset(["date", "status", "custom", "sort", "didFilter"], true);
    close();
  };

  const apply = evt => {
    state.didFilter = true;
    activity.update(state);
    close();
  };

  return (
    <div className="filter-bar-buttons button-container flex-container flex-horizontal">
      <UIButton className="secondary-button" onClick={ resetBtn }>Reset</UIButton>
      <UIButton className="primary-button" onClick={ apply }>Apply</UIButton>
    </div>
  );
}
